import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { AuthService, SharedService, getNotificationSocketConnection } from '@app/core';

export interface IUploadNotifyChip {
  companyId: string;
  shortName: string;
  name: string;
  parentCompanyId: string;
  parentCompanyName: string;
  entityName: string;
  count: number;
}

/**
 * Shared admin upload sticky-bar state + socket (list page only UI).
 * Socket stays alive in root so events are not lost while navigating review.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminUploadNotifyService implements OnDestroy {
  readonly chips$ = new BehaviorSubject<IUploadNotifyChip[]>([]);

  private socket: Socket | null = null;
  private byCompany = new Map<string, IUploadNotifyChip>();
  private started = false;
  /** After Dismiss, bar stays hidden until the next upload event arrives. */
  private dismissedUntilNextUpload = false;

  constructor(
    private authService: AuthService,
    private sharedService: SharedService,
    private ngZone: NgZone
  ) {}

  /** Idempotent — safe to call from list ngOnInit. */
  ensureConnected(): void {
    if (this.started || !this.authService.user?.isAdmin || !this.authService.token) {
      return;
    }

    const connection = getNotificationSocketConnection(this.authService.token, []);
    if (!connection) {
      return;
    }

    this.started = true;
    this.socket = io(connection.url, connection.options);
    this.socket.on('admin_invoice_uploaded', (payload: {
      invoice_id?: string;
      company_id?: string;
      company_short_name?: string;
      company_name?: string;
      parent_company_id?: string;
      parent_company_name?: string;
      entity_name?: string;
    }) => {
      this.ngZone.run(() => this.onUploaded(payload));
    });
  }

  dismiss(): void {
    this.byCompany.clear();
    this.dismissedUntilNextUpload = true;
    this.chips$.next([]);
  }

  chipHoverTitle(chip: IUploadNotifyChip): string {
    const parent = (chip.parentCompanyName || '').trim();
    const entity = (chip.entityName || chip.name || '').trim();
    if (parent && entity && parent !== entity) {
      return `${parent} + ${entity}`;
    }
    return entity || parent || chip.shortName || 'Company';
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.off('admin_invoice_uploaded');
      this.socket.disconnect();
      this.socket = null;
    }
    this.started = false;
  }

  private onUploaded(payload: {
    invoice_id?: string;
    company_id?: string;
    company_short_name?: string;
    company_name?: string;
    parent_company_id?: string;
    parent_company_name?: string;
    entity_name?: string;
  }): void {
    const companyId = payload?.company_id;
    if (!companyId) {
      return;
    }

    // Dismiss hid the bar; the next upload starts a fresh chip set and shows it again.
    if (this.dismissedUntilNextUpload) {
      this.byCompany.clear();
      this.dismissedUntilNextUpload = false;
    }

    const companyName = (payload.company_name || '').trim();
    const entityName = (payload.entity_name || companyName || '').trim();
    const parentCompanyId = (payload.parent_company_id || companyId || '').trim();
    const parentCompanyName = (payload.parent_company_name || companyName || '').trim();
    const fromPayload = (payload.company_short_name || '').trim();
    const fromInitials = companyName
      ? (this.sharedService.shortName(companyName) || '').replace(/undefined/g, '')
      : '';
    const shortName = fromPayload
      || fromInitials
      || (companyName.length > 24 ? `${companyName.slice(0, 24)}…` : companyName)
      || 'Company';
    const existing = this.byCompany.get(companyId);

    if (existing) {
      existing.count += 1;
      existing.shortName = shortName || existing.shortName;
      existing.name = companyName || existing.name;
      existing.entityName = entityName || existing.entityName;
      existing.parentCompanyId = parentCompanyId || existing.parentCompanyId;
      existing.parentCompanyName = parentCompanyName || existing.parentCompanyName;
    } else {
      this.byCompany.set(companyId, {
        companyId,
        shortName,
        name: companyName,
        parentCompanyId,
        parentCompanyName,
        entityName,
        count: 1
      });
    }

    // Newest / highest-count first so the left side of a long scroll stays useful.
    const chips = Array.from(this.byCompany.values())
      .sort((a, b) => b.count - a.count || a.shortName.localeCompare(b.shortName));
    this.chips$.next(chips);
  }
}
